import { useEffect, useRef, useState, type ReactElement } from "react";
import { Transition } from "react-transition-group";
import { Overlay } from "../ui/Overlay/Overlay";
import { Icon } from "../ui/Icon/Icon";
import { SignOutIcon } from "../ui/Icon/SignOutIcon";
import { avatarColor, avatarInitials } from "../lib/avatar";
import { useIdentity } from "../lib/user";
import styles from "./UserMenu.module.css";

const MENU_MS = 175;

export interface UserMenuProps {
  onSignOut: () => void;
  onAccount?: () => void;
  compact?: boolean;
}

export function UserMenu({
  onSignOut,
  onAccount,
  compact = false,
}: UserMenuProps): ReactElement {
  const user = useIdentity();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const secondary = user.org || user.email;

  // Desktop popover only: dismiss on outside click / Escape. The mobile sheet
  // is an Overlay, which handles both itself.
  useEffect(() => {
    if (!open || compact) return;
    function onPointer(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, compact]);

  const trigger = (
    <button
      type="button"
      className={[styles.trigger, compact ? styles.compact : ""]
        .filter(Boolean)
        .join(" ")}
      onClick={() => setOpen((v) => !v)}
      aria-haspopup={compact ? "dialog" : "menu"}
      aria-expanded={open}
      aria-label={compact ? "Account menu" : undefined}
    >
      <span
        className={styles.avatar}
        style={{ background: avatarColor(user) }}
        aria-hidden="true"
      >
        {avatarInitials(user)}
      </span>
      {compact ? null : (
        <>
          <span className={styles.identity}>
            <span className={styles.name}>{user.name || user.email}</span>
            <span className={styles.secondary}>{secondary}</span>
          </span>
          <Icon name="chevron" size={16} className={styles.chev} />
        </>
      )}
    </button>
  );

  if (compact) {
    return (
      <div className={styles.root}>
        {trigger}
        <Overlay
          open={open}
          onClose={() => setOpen(false)}
          placement="sheet-bottom"
          label="Account menu"
          panelStyle={{ padding: "20px 16px 24px" }}
        >
          <div className={styles.sheet}>
            <div className={styles.grab} />
            <div className={styles.sheetHeader}>
              <span
                className={`${styles.avatar} ${styles.sheetAvatar}`}
                style={{ background: avatarColor(user) }}
                aria-hidden="true"
              >
                {avatarInitials(user)}
              </span>
              <div className={styles.identity}>
                <p className={styles.sheetName}>{user.name || user.email}</p>
                <p className={styles.sheetEmail}>{user.email}</p>
              </div>
            </div>
            {onAccount ? (
              <button
                type="button"
                className={styles.sheetBtn}
                onClick={() => {
                  setOpen(false);
                  onAccount();
                }}
              >
                Account
              </button>
            ) : null}
            <button
              type="button"
              className={`${styles.sheetBtn} ${styles.sheetDanger}`}
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              Sign out
            </button>
          </div>
        </Overlay>
      </div>
    );
  }

  return (
    <div className={styles.root} ref={rootRef}>
      {trigger}
      <Transition
        in={open}
        timeout={MENU_MS}
        nodeRef={popoverRef}
        mountOnEnter
        unmountOnExit
      >
        {(state) => (
          <div
            ref={popoverRef}
            role="menu"
            className={`${styles.popover} ${styles.up} ${
              state === "entering" || state === "entered"
                ? styles.popoverShown
                : ""
            }`}
          >
            <div className={styles.header}>
              <p className={styles.headerName}>{user.name || user.email}</p>
              <p className={styles.headerEmail}>{user.email}</p>
            </div>
            <div className={styles.items}>
              {onAccount ? (
                <button
                  type="button"
                  role="menuitem"
                  className={styles.item}
                  onClick={() => {
                    setOpen(false);
                    onAccount();
                  }}
                >
                  <Icon name="account" size={18} />
                  Account
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className={`${styles.item} ${styles.danger}`}
                onClick={() => {
                  setOpen(false);
                  onSignOut();
                }}
              >
                <SignOutIcon size={18} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </Transition>
    </div>
  );
}
